import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import PaymentWorldlineConnected from '../PaymentWorldlineConnected'

import Loader from 'src/comps/Atom/Loader'
import { WORLDLINE_PAYMENT_TOKENIZER_SCRIPT } from './constants'
import styles from './styles.module.css'

import PrimaryButton from 'src/comps/Atom/Buttons/PrimaryButton'

const PaymentWorldline = ({
  src,
  onSuccess,
  onError,
  onValidationError,
  onBeforeSubmit,
  disabled,
}) => {
  const intl = useIntl()
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [tokenizer, setTokenizer] = useState(null)
  const [tokenizerScript, setTokenizerScript] = useState(null)
  const isVisible = useRef(false)
  const currentSrc = useRef(src)

  const handleOnError = error => {
    onError(`Worldline: ${error}`)
  }

  const cleanupPreviousTokenizer = () => {
    if (tokenizer) {
      setTokenizer(null)
    }

    const container = document.getElementById('div-hosted-tokenization')
    if (container) {
      container.innerHTML = ''
    }

    setIsLoading(true)
  }

  const initializeTokenizer = () => {
    const tokenizationUrl = src

    if (isVisible.current && document.getElementById('div-hosted-tokenization')) {
      try {
        // eslint-disable-next-line no-undef
        const newTokenizer = new Tokenizer(tokenizationUrl, 'div-hosted-tokenization', {
          hideCardholderName: false,
        })

        newTokenizer
          .initialize()
          .then(() => {
            setTokenizer(newTokenizer)
            setIsLoading(false)
            currentSrc.current = src
          })
          .catch(error => {
            handleOnError(`Failed to initialize tokenizer: ${error?.message ?? error}`)
          })
      } catch (error) {
        handleOnError(`Error creating tokenizer: ${error?.message ?? error}`)
      }
    }
  }

  useEffect(() => {
    isVisible.current = true
    return () => {
      isVisible.current = false
    }
  }, [])

  useEffect(() => {
    if (isVisible.current) {
      if (!tokenizerScript) {
        const script = document.createElement('script')
        script.src = WORLDLINE_PAYMENT_TOKENIZER_SCRIPT
        script.async = true
        script.onload = () => initializeTokenizer()
        script.onerror = () => {
          handleOnError('Failed to load payment script')
          setIsLoading(false)
        }
        setTokenizerScript(script)
      } else {
        document.body.appendChild(tokenizerScript)
      }
    }

    return () => {
      if (tokenizerScript && document.body.contains(tokenizerScript)) {
        document.body.removeChild(tokenizerScript)
        setTokenizerScript(null)
      }
    }
  }, [isVisible, tokenizerScript]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (src !== currentSrc.current && tokenizerScript && isVisible.current) {
      cleanupPreviousTokenizer()
      initializeTokenizer()
    }
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(event) {
    event.preventDefault()
    if (!tokenizer) {
      handleOnError('Tokenizer not initialized')
      return
    }

    setIsSubmitting(true)

    if (typeof onBeforeSubmit === 'function') {
      if (!(await onBeforeSubmit())) {
        setIsSubmitting(false)
        return
      }
      setIsSubmitting(false)
    }

    tokenizer.submitTokenization().then(result => {
      if (result.success) {
        onSuccess({
          hostedTokenizationId: result.hostedTokenizationId,
        })
      } else {
        setIsSubmitting(false)

        const errorMessage =
          typeof result.error?.message === 'string'
            ? result.error?.message
            : String(result.error?.message)

        // don't show error message for field validation errors
        if (
          errorMessage.includes(
            'An error occurred: Please check if all fields are correctly filled.'
          )
        ) {
          onValidationError()
        } else {
          handleOnError(result.error.message ?? result.error)
        }
      }
    })
  }

  return (
    <>
      {isLoading && <Loader />}
      <div
        className={styles.iframe}
        id="div-hosted-tokenization"
        data-testid="payment-iframe"
        ref={isVisible}
      />
      <PrimaryButton
        data-testid="payment-custom-submit"
        disabled={isLoading || disabled || isSubmitting}
        onClick={handleSubmit}
        className={styles.submitButton}
        tabIndex="0"
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleSubmit(event)
          }
        }}
      >
        {intl.formatMessage({ id: 'global.confirm-booking' })}
      </PrimaryButton>
    </>
  )
}

PaymentWorldline.propTypes = {
  src: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
  onBeforeSubmit: PropTypes.func,
  disabled: PropTypes.bool,
}

export default PaymentWorldlineConnected(PaymentWorldline)
